import { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./App.css";
import AtletaSearch from "./AtletaSearch";
import Manifestazioni from "./Manifestazioni";
import Login from "./Login";
import AdminPanel from "./AdminPanel";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const CATEGORIE = ["Assoluto", "Juniores", "Ragazzi", "Esordienti", "Senior"];
const TIPI = ["ITA", "EUR", "WR"];

function StatCard({ label, value, color }) {
  return (
    <div className="stat-card" style={{ '--stat-accent': color }}>
      <div className="stat-value">{value?.toLocaleString()}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

export default function App() {
  const [role, setRole] = useState(() => sessionStorage.getItem("role") || null);
  const [userPassword, setUserPassword] = useState(() => sessionStorage.getItem("pwd") || "");
  const [showAdmin, setShowAdmin] = useState(false);

  const [stats, setStats] = useState({});
  const [records, setRecords] = useState([]);
  const [eventi, setEventi] = useState([]);
  const [classi, setClassi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("darkMode") === "true");
  const [lastScrapeDate, setLastScrapeDate] = useState(null);
  const [lastPdfUpdate, setLastPdfUpdate] = useState(null);
  const [tab, setTab] = useState("manifestazioni");
  const [filters, setFilters] = useState({
    sesso: "",
    tipo_record: "ITA",
    vasca: "",
    categoria: "Assoluto",
    evento: "",
    classe: "",
    atleta: "",
  });

  const [atletaInput, setAtletaInput] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    document.body.classList.toggle("dark", darkMode);
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    if (!role) return;
    axios.get(`${API}/stats`).then(r => setStats(r.data));
    axios.get(`${API}/records/eventi`).then(r => setEventi(r.data));
    axios.get(`${API}/records/classi`).then(r => setClassi(r.data));
    axios.get(`${API}/info`).then(r => {
      setLastScrapeDate(r.data.last_scrape_date);
      setLastPdfUpdate(r.data.last_pdf_update);
    });
  }, [role]);

  useEffect(() => {
    if (!role) return;
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== "")
    );
    setLoading(true);
    axios.get(`${API}/records`, { params })
      .then(r => setRecords(r.data))
      .finally(() => setLoading(false));
  }, [filters, role]);

  const set = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const handleLogin = (r, pwd) => {
    setRole(r);
    setUserPassword(pwd);
    sessionStorage.setItem("role", r);
    sessionStorage.setItem("pwd", pwd);
  };

  const handleLogout = () => {
    setRole(null);
    setUserPassword("");
    sessionStorage.removeItem("role");
    sessionStorage.removeItem("pwd");
  };

  if (!role) return <Login onLogin={handleLogin} darkMode={darkMode} onToggleDark={() => setDarkMode(d => !d)} />;

  return (
    <div className="app">
      <a href="#main-content" className="skip-link">Vai al contenuto principale</a>

      <header className="header">
        <h1 className="sr-only">DigiSwimP — Risultati nuoto paralimpico FINP</h1>
        <div className="header-center">
          <div className="header-logos">
            <img
              src="https://www.finp.it/images/logofinp.png"
              alt="Logo FINP"
              className="header-finp-logo"
            />
            <img src="/logoDSP.png" alt="DigiSwimP" className="header-dsp-logo" />
          </div>
          <p>Fonte: Federazione Italiana Nuoto Paralimpico
            {lastScrapeDate && <span className="last-update"> · Aggiornato al {lastScrapeDate}</span>}
            {lastPdfUpdate && <span className="last-update"> · Record aggiornati al {lastPdfUpdate}</span>}
          </p>
        </div>
      </header>

      <div className="tabs">
        <div role="tablist" aria-label="Sezioni principali" className="tabs-list">
          <button
            role="tab"
            id="tab-manifestazioni"
            aria-selected={tab === "manifestazioni"}
            aria-controls="panel-manifestazioni"
            className={tab === "manifestazioni" ? "active" : ""}
            onClick={() => setTab("manifestazioni")}
          >Manifestazioni</button>
          <button
            role="tab"
            id="tab-records"
            aria-selected={tab === "records"}
            aria-controls="panel-records"
            className={tab === "records" ? "active" : ""}
            onClick={() => setTab("records")}
          >Record FINP</button>
          <button
            role="tab"
            id="tab-atleta"
            aria-selected={tab === "atleta"}
            aria-controls="panel-atleta"
            className={tab === "atleta" ? "active" : ""}
            onClick={() => setTab("atleta")}
          >Atleti</button>
        </div>
        {role === "admin" && (
          <button className="tab-admin" onClick={() => setShowAdmin(true)}>Admin</button>
        )}
        <div className="tabs-actions">
          <button
            className="icon-btn"
            onClick={() => setDarkMode(d => !d)}
            aria-label={darkMode ? "Modalità chiara" : "Modalità scura"}
            title={darkMode ? "Modalità chiara" : "Modalità scura"}
          >
            {darkMode
              ? <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              : <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            }
          </button>
          <button
            className="icon-btn icon-btn-logout"
            onClick={handleLogout}
            aria-label="Esci"
            title="Esci"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </div>
      </div>

      {showAdmin && (
        <AdminPanel adminPassword={userPassword} onClose={() => setShowAdmin(false)} />
      )}

      <main id="main-content">
        {tab === "manifestazioni" && (
          <div role="tabpanel" id="panel-manifestazioni" aria-labelledby="tab-manifestazioni">
            <Manifestazioni />
          </div>
        )}

        {tab === "atleta" && (
          <div role="tabpanel" id="panel-atleta" aria-labelledby="tab-atleta">
            <AtletaSearch />
          </div>
        )}

        {tab === "records" && (
          <div role="tabpanel" id="panel-records" aria-labelledby="tab-records">
            <div className="stats-row">
              <StatCard label="Record totali"    value={stats.totale_record}   color="var(--thermocline)" />
              <StatCard label="Atleti"           value={stats.atleti_unici}    color="var(--thermocline-text)" />
              <StatCard label="Record italiani"  value={stats.record_italiani} color="var(--record-ita)" />
              <StatCard label="Record mondiali"  value={stats.record_mondiali} color="var(--record-wr)" />
            </div>
            <div className="filters">
              <select
                value={filters.sesso}
                onChange={e => set("sesso", e.target.value)}
                aria-label="Filtra per sesso"
              >
                <option value="">Tutti i sessi</option>
                <option value="F">Femminile</option>
                <option value="M">Maschile</option>
                <option value="X">Misto</option>
              </select>

              <select
                value={filters.tipo_record}
                onChange={e => set("tipo_record", e.target.value)}
                aria-label="Filtra per tipo record"
              >
                <option value="">Tutti i tipi</option>
                {TIPI.map(t => <option key={t} value={t}>{t === "ITA" ? "Italiano" : t === "EUR" ? "Europeo" : "Mondiale"}</option>)}
              </select>

              <select
                value={filters.vasca}
                onChange={e => set("vasca", e.target.value)}
                aria-label="Filtra per vasca"
              >
                <option value="">Tutte le vasche</option>
                <option value="25">25 mt</option>
                <option value="50">50 mt</option>
              </select>

              <select
                value={filters.categoria}
                onChange={e => set("categoria", e.target.value)}
                aria-label="Filtra per categoria"
              >
                <option value="">Tutte le categorie</option>
                {CATEGORIE.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              <select
                value={filters.evento}
                onChange={e => set("evento", e.target.value)}
                aria-label="Filtra per specialità"
              >
                <option value="">Tutte le specialità</option>
                {eventi.map(e => <option key={e} value={e}>{e}</option>)}
              </select>

              <select
                value={filters.classe}
                onChange={e => set("classe", e.target.value)}
                aria-label="Filtra per classe"
              >
                <option value="">Tutte le classi</option>
                {classi.map(c => <option key={c} value={c}>{c}</option>)}
              </select>

              <input
                type="text"
                placeholder="Cerca atleta..."
                value={atletaInput}
                aria-label="Filtra per nome atleta"
                onChange={e => {
                  const val = e.target.value;
                  setAtletaInput(val);
                  clearTimeout(debounceRef.current);
                  debounceRef.current = setTimeout(() => set("atleta", val), 400);
                }}
              />
            </div>

            <div className="results-info" role="status" aria-live="polite">
              {loading ? "Caricamento..." : `${records.length} record trovati`}
            </div>

            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Specialità</th>
                    <th>Classe</th>
                    <th>Atleta</th>
                    <th>Tempo</th>
                    <th>Data</th>
                    <th>Luogo</th>
                    <th>Vasca</th>
                    <th>Tipo</th>
                    <th>Categoria</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id}>
                      <td>{r.evento}</td>
                      <td><span className="badge" title={`Classe ${r.classe} — classificazione funzionale FINP`}>{r.classe}</span></td>
                      <td
                        className={r.nome_atleta === "VACANTE" ? "vacante" : ""}
                        title={r.nome_atleta === "VACANTE" ? "Nessun record registrato per questa classe" : undefined}
                      >{r.nome_atleta}</td>
                      <td className="tempo">{r.tempo}</td>
                      <td>{r.data_record}</td>
                      <td>{r.luogo}</td>
                      <td>{r.vasca}m</td>
                      <td><span
                        className={`tipo tipo-${r.tipo_record}`}
                        title={r.tipo_record === "ITA" ? "Record Italiano" : r.tipo_record === "EUR" ? "Record Europeo" : "Record Mondiale"}
                      >{r.tipo_record}</span></td>
                      <td>{r.categoria}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
